import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from 'src/app/data.service';
import { firstValueFrom } from 'rxjs';

/**
 *  Kinevezések: a kinevezés-fajták karbantartása és hozzárendelése a kollégákhoz.
 *
 *  A fajták TANÉVHEZ kötöttek (`orakedvezmeny`), a hozzárendelés is (`kinevezes`).
 *  Ez fontos: ha egy új tanévre nem készülnek el a fajták, a tantárgyfelosztás nem
 *  lát óraszám-kedvezményt, hiába van hozzárendelés – ezért van „Átemelés előző
 *  tanévről" gomb, és ezért mutatjuk mindig, melyik tanévet szerkesztjük.
 *
 *  Az osztályfőnök külön eset: nem elég a kinevezés, az `osztaly.of` mezőben is
 *  rögzíteni kell, kié az osztály. A két adat elcsúszhat, ezt az eltérés-panel mutatja.
 */
@Component({
  selector: 'app-admin-kinevezes',
  templateUrl: './kinevezes.component.html',
  styleUrls: ['./kinevezes.component.scss'],
})
export class AdminKinevezesComponent implements OnInit {

  intezmeny = '';
  ev: number = new Date().getFullYear();

  fajtak: any[] = [];
  hozzarendelesek: any[] = [];
  munkatarsak: any[] = [];
  ellenorzes: any = { osztaly_kinevezes_nelkul: [], kinevezes_osztaly_nelkul: [] };

  ujFajta: any = { nev: '', kod: '', ora: 0 };
  ujHozzarendeles: any = { munkatars: '', orakedvezmeny: '' };
  szerkesztett: any = null;

  /** A kolléga-választó keresőszava (a lista több száz fős is lehet). */
  munkatarsSzuro = '';

  /*  Alapkészlet: az iskolákban szokásos kinevezések. Órakedvezmény NÉLKÜL (0)
   *  kerülnek be, mert az intézményenként eltér – utána a táblában szerkeszthetők
   *  és törölhetők. Azért van rá gomb, mert üres listával nincs mit szerkeszteni,
   *  az „Átemelés előző tanévről" pedig új intézménynél nem talál forrást.
   */
  readonly alapkeszlet = [
    { nev: 'Osztályfőnök', kod: 'OF' },
    { nev: 'Munkaközösség-vezető', kod: 'MK' },
    { nev: 'Diákönkormányzatot segítő', kod: 'DOK' },
    { nev: 'Gyermek- és ifjúságvédelmi felelős', kod: 'GYIV' },
    { nev: 'Munkavédelmi felelős', kod: 'MV' },
    { nev: 'Rendszergazda', kod: 'RG' },
  ];

  toltodik = false;
  fut = false;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const sajat = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = sajat?.intezmenyId || '';
    this.ev = (await this.data.getMunkatanev()) || sajat?.aktualistanev || new Date().getFullYear();
    await this.betolt();
  }

  private get alap() {
    return `${this.ev}/${this.intezmeny}`;
  }

  async betolt() {
    if (!this.intezmeny) return;
    this.toltodik = true;
    try {
      const [fajtak, hozzarendelesek, ellenorzes, munkatarsak]: any = await Promise.all([
        firstValueFrom(this.http.get(`/api/kinevezes/fajtak/${this.alap}`)),
        firstValueFrom(this.http.get(`/api/kinevezes/hozzarendelesek/${this.alap}`)),
        firstValueFrom(this.http.get(`/api/kinevezes/ellenorzes/${this.alap}`)),
        firstValueFrom(this.data.getAllomany(this.intezmeny)),
      ]);
      this.fajtak = fajtak || [];
      this.hozzarendelesek = hozzarendelesek || [];
      this.ellenorzes = ellenorzes || { osztaly_kinevezes_nelkul: [], kinevezes_osztaly_nelkul: [] };
      this.munkatarsak = Array.isArray(munkatarsak) ? munkatarsak : [];
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A kinevezések betöltése nem sikerült.', 'error');
    } finally {
      this.toltodik = false;
    }
  }

  /** Kollégánként csoportosított hozzárendelés-lista (a táblához). */
  get kollegankent(): any[] {
    const map = new Map<string, any>();
    for (const h of this.hozzarendelesek) {
      if (!map.has(h.munkatars)) {
        map.set(h.munkatars, {
          munkatars: h.munkatars,
          nev: `${h.viselt_vez || ''} ${h.viselt_ker || ''}`.trim() || h.munkatars,
          tetelek: [],
          ossz_ora: 0,
        });
      }
      const sor = map.get(h.munkatars);
      sor.tetelek.push(h);
      sor.ossz_ora += Number(h.ora) || 0;
    }
    return [...map.values()].sort((a, b) => a.nev.localeCompare(b.nev, 'hu'));
  }

  /**
   *  A kolléga-választó listája: névsorban (magyar ábécé szerint), keresővel szűrve.
   *  A keresés a névre ÉS az azonosítóra is illeszkedik, mert a névsorban több
   *  azonos nevű kolléga is lehet.
   */
  get szurtMunkatarsak(): any[] {
    const nev = (m: any) => `${m?.viselt_vez || ''} ${m?.viselt_ker || ''}`.trim();
    const keres = this.munkatarsSzuro.trim().toLowerCase();
    return (this.munkatarsak || [])
      .filter((m: any) => !keres
        || nev(m).toLowerCase().includes(keres)
        || String(m?.adid || '').toLowerCase().includes(keres))
      .sort((a: any, b: any) => nev(a).localeCompare(nev(b), 'hu'));
  }

  munkatarsNev(adid: string): string {
    const m = this.munkatarsak.find((x: any) => String(x.adid) === String(adid));
    return m ? `${m.viselt_vez || ''} ${m.viselt_ker || ''}`.trim() : adid;
  }

  /* ── fajták ── */

  async fajtaFelvesz() {
    if (!this.ujFajta.nev?.trim() || !this.ujFajta.kod?.trim()) {
      this.data.sendToast('A név és a kód kötelező.', 'warning');
      return;
    }
    this.fut = true;
    try {
      await firstValueFrom(this.http.post('/api/kinevezes/fajta', {
        ...this.ujFajta, ev: this.ev, intezmeny: this.intezmeny,
      }));
      this.ujFajta = { nev: '', kod: '', ora: 0 };
      await this.betolt();
      this.data.sendToast('Kinevezés-fajta felvéve.');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A felvétel nem sikerült.', 'error');
    } finally { this.fut = false; }
  }

  async fajtaMentes(f: any) {
    this.fut = true;
    try {
      await firstValueFrom(this.http.put(`/api/kinevezes/fajta/${f.id}`,
        { nev: f.nev, kod: f.kod, ora: f.ora }));
      this.szerkesztett = null;
      await this.betolt();
      this.data.sendToast('Mentve.');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    } finally { this.fut = false; }
  }

  async fajtaTorles(f: any) {
    if (!confirm(`Biztosan törlöd: ${f.nev} (${f.kod})?`)) return;
    this.fut = true;
    try {
      await firstValueFrom(this.http.delete(`/api/kinevezes/fajta/${f.id}`));
      await this.betolt();
      this.data.sendToast('Törölve.');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
    } finally { this.fut = false; }
  }

  async atemeles() {
    const forras = Number(prompt('Melyik tanévről emeljük át a kinevezés-fajtákat?', String(this.ev - 1)));
    if (!forras) return;
    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post('/api/kinevezes/fajtak/masolas',
        { ev: this.ev, forras_ev: forras, intezmeny: this.intezmeny }));
      await this.betolt();
      this.data.sendToast(r?.felvett
        ? `${r.felvett} kinevezés-fajta átemelve a(z) ${forras}-ös tanévről.`
        : 'Nem volt mit átemelni – minden fajta megvan ebben a tanévben.', 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az átemelés nem sikerült.', 'error');
    } finally { this.fut = false; }
  }

  /** Az iskolákban szokásos kinevezés-fajták felvétele, hogy legyen mit szerkeszteni. */
  async alapkeszletFelvesz() {
    const megvan = new Set(this.fajtak.map((f: any) => String(f.kod || '').toUpperCase()));
    const felveendo = this.alapkeszlet.filter((a) => !megvan.has(a.kod));
    if (!felveendo.length) {
      this.data.sendToast('Az alapkészlet minden eleme megvan ebben a tanévben.', 'success');
      return;
    }
    if (!confirm(`${felveendo.length} kinevezés-fajta felvétele órakedvezmény nélkül `
      + `(${felveendo.map((a) => a.kod).join(', ')}). Utána a táblában szerkesztheted őket. Folytatod?`)) return;
    this.fut = true;
    let ok = 0;
    try {
      for (const a of felveendo) {
        try {
          await firstValueFrom(this.http.post('/api/kinevezes/fajta',
            { nev: a.nev, kod: a.kod, ora: 0, ev: this.ev, intezmeny: this.intezmeny }));
          ok++;
        } catch (e) { /* a már meglévőt a backend 409-cel utasítja el – nem hiba */ }
      }
      await this.betolt();
      this.data.sendToast(`${ok} kinevezés-fajta felvéve. Az órakedvezményt a táblában állítsd be.`, 'success');
    } finally { this.fut = false; }
  }

  /* ── hozzárendelés ── */

  /**
   *  Hiányzó OF-kinevezések pótlása egyben.
   *
   *  Az osztályfőnök beállításakor a kinevezés csak akkor jön létre magától, ha az
   *  adott tanévben már van „OF" kódú fajta. Ha nincs, az osztályok osztályfőnöke
   *  beáll, a kinevezések viszont elmaradnak – ezeket pótolja ez a gomb.
   */
  async ofPotlas() {
    const hianyzik = this.ellenorzes?.osztaly_kinevezes_nelkul?.length || 0;
    if (!hianyzik) {
      this.data.sendToast('Nincs pótolni való: minden osztályfőnöknek megvan a kinevezése.', 'success');
      return;
    }
    if (!confirm(`${hianyzik} osztálynál hiányzik az OF-kinevezés. Pótoljuk mindet a(z) ${this.ev}-es tanévre?`)) return;
    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post('/api/kinevezes/osztalyfonok-potlas',
        { ev: this.ev, intezmeny: this.intezmeny }));
      await this.betolt();
      const fajtaResz = r?.fajta_letrehozva
        ? ' Az „OF" kinevezés-fajta most jött létre, órakedvezmény nélkül – állítsd be a táblában.'
        : '';
      this.data.sendToast(`${r?.potolva || 0} OF-kinevezés pótolva.${fajtaResz}`, 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A pótlás nem sikerült.', 'error');
    } finally { this.fut = false; }
  }

  async hozzarendel() {
    const { munkatars, orakedvezmeny } = this.ujHozzarendeles;
    if (!munkatars || !orakedvezmeny) {
      this.data.sendToast('Válaszd ki a kollégát és a kinevezést.', 'warning');
      return;
    }
    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post('/api/kinevezes/hozzarendeles',
        { munkatars, orakedvezmeny, ev: this.ev, intezmeny: this.intezmeny }));
      this.ujHozzarendeles = { munkatars: '', orakedvezmeny: '' };
      await this.betolt();
      this.data.sendToast(r?.mar_megvolt ? 'Ez a kinevezés már megvolt.' : 'Kinevezés hozzárendelve.');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A hozzárendelés nem sikerült.', 'error');
    } finally { this.fut = false; }
  }

  async hozzarendelesTorles(h: any) {
    this.fut = true;
    try {
      await firstValueFrom(this.http.delete(`/api/kinevezes/hozzarendeles/${h.id}`));
      await this.betolt();
      this.data.sendToast('Kinevezés visszavonva.');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A visszavonás nem sikerült.', 'error');
    } finally { this.fut = false; }
  }

  get vanEltere(): boolean {
    return (this.ellenorzes?.osztaly_kinevezes_nelkul?.length || 0) > 0
      || (this.ellenorzes?.kinevezes_osztaly_nelkul?.length || 0) > 0;
  }
}
